import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useLanguage, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { ARAB_SANAD_LOGO, ARAB_SANAD_LOGO_FALLBACK, NY_SUNSET_BG, NY_SUNSET_BG_FALLBACK } from "@/lib/brand";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t("يرجى ملء جميع الحقول", "Please fill all fields")); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      login(res.data.user, res.data.access_token);
      toast.success(t("تم تسجيل الدخول بنجاح!", "Login successful!"));
      const role = res.data.user.role;
      navigate(["admin", "owner", "support", "viewer", "video_contributor"].includes(role) ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("فشل تسجيل الدخول", "Login failed"));
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 overflow-hidden" style={{ background: "var(--bg-base)" }}>
      {/* Left: NY sunset background — hidden on mobile */}
      <div className="relative hidden lg:block">
        <img
          src={NY_SUNSET_BG}
          alt="New York skyline at sunset with the Statue of Liberty"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "left center" }}
          loading="eager"
          onError={(e) => { if (e.target.src !== NY_SUNSET_BG_FALLBACK) e.target.src = NY_SUNSET_BG_FALLBACK; }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/45 via-black/15 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 z-10 text-white">
          <img
            src={ARAB_SANAD_LOGO}
            alt="Arab Sanad"
            className="w-32 mb-6 drop-shadow-2xl"
            onError={(e) => { if (e.target.src !== ARAB_SANAD_LOGO_FALLBACK) e.target.src = ARAB_SANAD_LOGO_FALLBACK; }}
          />
          <h2 className="font-['Cairo'] font-black text-4xl xl:text-5xl mb-3 leading-tight drop-shadow-lg">
            {t("منصة عرب سند", "Arab Sanad")}
          </h2>
          <p className="text-base xl:text-lg opacity-95 max-w-md drop-shadow">
            {t("المنصة المتكاملة للعرب في الولايات المتحدة", "The integrated platform for Arabs in the United States")}
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex flex-col justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto animate-fade-in" data-testid="login-form">
          {/* Logo on mobile + heading */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-5">
              <img
                src={ARAB_SANAD_LOGO}
                alt="Arab Sanad"
                className="w-24 mx-auto"
                onError={(e) => { if (e.target.src !== ARAB_SANAD_LOGO_FALLBACK) e.target.src = ARAB_SANAD_LOGO_FALLBACK; }}
              />
            </Link>
            <h1 className="font-['Cairo'] text-3xl md:text-4xl font-black" style={{ color: "var(--text-primary)" }} data-testid="login-heading">
              {t("تسجيل الدخول", "Sign in")}
            </h1>
            <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              {t("مرحباً بك في منصة عرب سند", "Welcome back to Arab Sanad")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
                {t("البريد الإلكتروني", "Email")}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("أدخل بريدك الإلكتروني", "Enter your email")}
                  className="w-full h-12 px-4 pr-11 rounded-2xl text-sm transition-all"
                  data-testid="login-email"
                />
                <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2" size={18} style={{ color: "var(--accent-gold)" }} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
                {t("كلمة المرور", "Password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("أدخل كلمة المرور", "Enter your password")}
                  className="w-full h-12 px-4 pr-11 pl-11 rounded-2xl text-sm"
                  data-testid="login-password"
                />
                <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2" size={18} style={{ color: "var(--accent-gold)" }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute left-3 top-1/2 -translate-y-1/2"
                        style={{ color: "var(--text-muted)" }} aria-label="toggle password">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="text-left mt-2">
                <Link to="/forgot-password" className="text-xs font-semibold hover:underline" style={{ color: "var(--accent-gold-hover)" }} data-testid="forgot-password-link">
                  {t("نسيت كلمة المرور؟", "Forgot password?")}
                </Link>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full h-12 text-base" data-testid="login-submit">
              {loading ? t("جاري الدخول…", "Signing in…") : t("تسجيل الدخول", "Sign in")}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t" style={{ borderColor: "var(--border-soft)" }} /></div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4" style={{ background: "var(--bg-base)", color: "var(--text-muted)" }}>{t("أو", "or")}</span>
            </div>
          </div>

          <button type="button" onClick={handleGoogleLogin} className="btn-secondary w-full h-12 inline-flex items-center justify-center gap-2 text-base" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("المتابعة مع Google", "Continue with Google")}
          </button>

          <p className="text-center mt-6 text-sm" style={{ color: "var(--text-secondary)" }}>
            {t("ليس لديك حساب؟", "Don't have an account?")}{" "}
            <Link to="/register" className="font-bold hover:underline" style={{ color: "var(--accent-gold-hover)" }} data-testid="register-link">
              {t("إنشاء حساب جديد", "Sign up")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
